import React, { useState } from "react";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Warning, Trash, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminDanger() {
  const { user, logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("Administrator");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  if (user?.role !== "admin") {
    return (
      <div className="p-8 max-w-xl mx-auto mt-12" data-testid="admin-danger-forbidden">
        <div className="rmms-card p-6 text-center">
          <ShieldWarning size={36} weight="duotone" className="mx-auto text-amber-500 mb-3" />
          <div className="overline text-rose-600">Admin only</div>
          <h2 className="text-xl font-heading font-semibold tracking-tight mt-1">You don't have permission to view this section.</h2>
          <a href="/" className="inline-block mt-4 text-sm text-[#002FA7] hover:underline">← Return to dashboard</a>
        </div>
      </div>
    );
  }

  const canSubmit =
    confirm === "WIPE-ALL-DATA" &&
    newEmail.includes("@") &&
    newPassword.length >= 6 &&
    !busy;

  const onWipe = async () => {
    if (!window.confirm("FINAL CONFIRMATION:\nThis will DELETE every user, job, store, brand, company, equipment, compliance doc, audit log entry and uploaded file.\n\nOnly the new admin you specified will remain.\n\nThis cannot be undone. Continue?")) return;
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const r = await http.post("/admin/wipe-all", {
        confirm,
        new_admin_email: newEmail.trim().toLowerCase(),
        new_admin_password: newPassword,
        new_admin_name: newName.trim() || "Administrator",
      });
      setResult(r.data);
      toast.success("Wipe complete. Signing you out…");
      setTimeout(() => logout(), 2500);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="admin-danger-page">
      <div className="mb-6">
        <div className="overline text-rose-600">Administration · Danger zone</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mt-1">Wipe all data</h1>
        <p className="text-sm text-slate-600 mt-2">
          Permanently deletes every record across every collection — users, jobs, stages, stores, brands, companies, equipment, compliance documents, maintenance companies, repair items, skills, approvals, audit logs, notifications, files, feedback, role limits and custom roles — and seeds <strong>one</strong> new admin you specify below.
        </p>
      </div>

      <div className="rmms-card border-2 border-rose-200 p-6" style={{ background: "linear-gradient(180deg, rgba(225,29,72,0.03) 0%, rgba(255,255,255,1) 60%)" }}>
        <div className="flex items-start gap-3 mb-5">
          <div className="h-10 w-10 rounded-md bg-rose-50 border border-rose-200 inline-flex items-center justify-center shrink-0">
            <Warning size={20} weight="fill" className="text-rose-600" />
          </div>
          <div>
            <div className="font-heading font-semibold text-rose-700">This action is irreversible.</div>
            <div className="text-xs text-slate-600 mt-1">
              Running this on <span className="font-mono-data">https://servinpri.com</span> will erase production data permanently. There is no rollback. Files in object storage are also deleted from the database index (orphaned blobs may remain in storage but are no longer referenced).
            </div>
          </div>
        </div>

        {result ? (
          <div data-testid="wipe-result" className="space-y-3">
            <div className="overline text-emerald-700">Wipe complete</div>
            <div className="text-sm">All data cleared. The only user now is:</div>
            <div className="font-mono-data text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">{result.new_admin}</div>
            <div className="text-xs text-slate-500">Signing you out — log back in with the new admin credentials.</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="overline mb-1.5 block">New admin email</label>
              <input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@yourdomain.com"
                type="email"
                autoComplete="off"
                data-testid="wipe-new-admin-email"
                className="rmms-input"
              />
            </div>
            <div>
              <label className="overline mb-1.5 block">New admin password (min 6 chars)</label>
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type="password"
                autoComplete="new-password"
                data-testid="wipe-new-admin-password"
                className="rmms-input"
              />
            </div>
            <div>
              <label className="overline mb-1.5 block">New admin name</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="wipe-new-admin-name"
                className="rmms-input"
              />
            </div>

            <div className="pt-3 border-t border-rose-100">
              <label className="overline mb-1.5 block text-rose-700">
                Type <span className="font-mono-data bg-rose-50 px-1.5 py-0.5 border border-rose-200 rounded-sm">WIPE-ALL-DATA</span> to confirm
              </label>
              <input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="WIPE-ALL-DATA"
                data-testid="wipe-confirm-input"
                className="rmms-input font-mono-data tracking-wider"
              />
            </div>

            {err && <div data-testid="wipe-error" className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}

            <div className="flex items-center justify-between gap-3 pt-2">
              <a href="/" className="text-sm text-slate-500 hover:text-slate-900">Cancel</a>
              <button
                onClick={onWipe}
                disabled={!canSubmit}
                data-testid="wipe-submit-button"
                className="h-10 px-5 text-sm font-medium rounded-md inline-flex items-center gap-2 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#E11D48", border: "1px solid #E11D48" }}
              >
                <Trash size={14} weight="bold" />
                {busy ? "Wiping…" : "Wipe all data"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 text-xs text-slate-500 space-y-1">
        <div><span className="font-heading uppercase tracking-wider font-semibold text-slate-700">Endpoint:</span> <span className="font-mono-data">POST /api/admin/wipe-all</span></div>
        <div><span className="font-heading uppercase tracking-wider font-semibold text-slate-700">Audit:</span> wipe events are logged to the backend service log with the admin's email.</div>
      </div>
    </div>
  );
}
